'use client';

/**
 * Gallery Media Tracking
 *
 * This module provides a unified interface for tracking media uploads and selections
 * in the CMS gallery. It consolidates functionality that was previously split across
 * separate image and video tracking modules.
 *
 * Key features:
 * - Combined interface for tracking both image and video uploads
 * - Simplified validation and error handling
 * - Support for recorder app uploads
 * - Support for image selection tracking
 */

import type { MediaGalleryListItem } from '@/services/httpServices/MediaGalleryServices/types';
import type { MediaType } from '@/tracking/shared/types';
import type { EventDataForType } from '@/utils/trackings-logger';
import { GTMEventType, sendGTMEvent } from '@/utils/trackings-logger';

// Combined location types
type GalleryLocation = 'gallery' | 'course_cover' | 'sequence_step' | 'recorder' | 'other';

// Combined source types
type GallerySource = 'computer' | 'url' | 'cloud_storage' | 'recorder' | 'recommended' | 'other';

// Base upload data structure shared by both image and video
type BaseMediaUploadData = {
  location: GalleryLocation | string;
  source: GallerySource | string;
  size: number;
  mimeType: string;
  space: string;
  uploadDuration: number;
  hash: string;
};

// Combined interface for upload events
export type MediaUploadData = {
  mediaType: MediaType;
} & BaseMediaUploadData;

/**
 * Validates and sanitizes upload data to ensure all fields have valid values
 *
 * @param data - The data to validate/sanitize
 * @returns Sanitized data with default values for any invalid fields
 */
function sanitizeUploadData(data: Partial<BaseMediaUploadData>): BaseMediaUploadData {
  const result: BaseMediaUploadData = {
    location: 'unknown',
    source: 'unknown',
    size: 0,
    mimeType: 'unknown',
    space: 'unknown',
    uploadDuration: 0,
    hash: 'unknown',
  };

  // Process string fields
  if (data.location !== null && data.location !== undefined && data.location !== '') {
    result.location = data.location;
  }

  if (data.source !== null && data.source !== undefined && data.source !== '') {
    result.source = data.source;
  }

  if (data.mimeType !== null && data.mimeType !== undefined && data.mimeType !== '') {
    result.mimeType = data.mimeType;
  }

  if (data.space !== null && data.space !== undefined && data.space !== '') {
    result.space = data.space;
  }

  if (data.hash !== null && data.hash !== undefined && data.hash !== '') {
    result.hash = data.hash;
  }

  // Process numeric fields
  if (data.size !== null && data.size !== undefined && Number.isNaN(data.size) === false) {
    result.size = data.size;
  }

  if (data.uploadDuration !== null
    && data.uploadDuration !== undefined
    && Number.isNaN(data.uploadDuration) === false) {
    result.uploadDuration = data.uploadDuration;
  }

  return result;
}

/**
 * Tracks when any media (image or video) is uploaded in the CMS
 *
 * This function handles both image and video uploads with a unified interface,
 * automatically formatting the payloads according to the expected GTM event structure.
 *
 * @param mediaType - Type of media ('image' or 'video')
 * @param uploadData - The upload data
 */
export function trackMediaUploaded(
  mediaType: MediaType,
  uploadData: Partial<BaseMediaUploadData>,
): void {
  const sanitizedData = sanitizeUploadData(uploadData);

  if (mediaType === 'image') {
    const payload: EventDataForType<GTMEventType.IMAGE_UPLOADED> = {
      imageUpload_location: sanitizedData.location,
      imageUpload_source: sanitizedData.source,
      imageUpload_size: sanitizedData.size,
      imageUpload_mime: sanitizedData.mimeType,
      imageUpload_space: sanitizedData.space,
      imageUpload_upload_duration: sanitizedData.uploadDuration,
      imageUpload_hash: sanitizedData.hash,
    };

    sendGTMEvent(GTMEventType.IMAGE_UPLOADED, payload);
  } else if (mediaType === 'video') {
    const payload: EventDataForType<GTMEventType.VIDEO_UPLOADED> = {
      VideoUploaded_Location: sanitizedData.location,
      VideoUploaded_Source: sanitizedData.source,
      VideoUploaded_Size: sanitizedData.size,
      VideoUploaded_Mime: sanitizedData.mimeType,
      VideoUploaded_Space: sanitizedData.space,
      VideoUploaded_UploadDuration: sanitizedData.uploadDuration,
      VideoUploaded_Hash: sanitizedData.hash,
    };

    sendGTMEvent(GTMEventType.VIDEO_UPLOADED, payload);
  }
}

/**
 * Simplified function to track media uploads with a single object parameter
 *
 * Example usage:
 * ```
 * trackUpload({
 *   mediaType: 'image',
 *   location: 'gallery',
 *   source: 'computer',
 *   size: 1024,
 *   mimeType: 'image/jpeg',
 *   space: 'my-space',
 *   uploadDuration: 500,
 *   hash: 'abc123'
 * });
 * ```
 *
 * @param data - Complete media upload data including the mediaType
 */
export function trackUpload(data: MediaUploadData): void {
  trackMediaUploaded(data.mediaType, data);
}

/**
 * Interface for recorder app upload event format
 */
type RecorderUploadSuccessful = {
  space: string;
  mime: string;
  uploadDuration: number;
  hash: string;
  size: number;
};

/**
 * Tracks uploads from the recorder app
 *
 * This function is designed to handle the recorder app's upload_successful event format,
 * converting it to the format expected by our tracking system.
 *
 * Example usage:
 * ```
 * trackRecorderUpload({
 *   space: 'my-space',
 *   mime: 'image/jpeg',
 *   uploadDuration: 500,
 *   hash: 'abc123',
 *   size: 1024
 * }, 'image');
 * ```
 *
 * @param recorderData - Data from the recorder app's upload_successful event
 * @param mediaType - Type of media being uploaded ('image' or 'video')
 */
export function trackRecorderUpload(
  recorderData: RecorderUploadSuccessful,
  mediaType: MediaType,
): void {
  trackMediaUploaded(mediaType, {
    location: 'recorder',
    source: 'recorder',
    size: recorderData.size,
    mimeType: recorderData.mime,
    space: recorderData.space,
    uploadDuration: recorderData.uploadDuration,
    hash: recorderData.hash,
  });
}

// Image selection tracking
type ImageSelectionLocation = 'step' | 'cover';

/**
 * Tracks when a user selects an image from the Media Gallery
 *
 * @param space - The name of the space the image was selected in
 * @param location - Where the image was selected for ('step' or 'cover')
 * @param isRecommended - Whether the selected image was a recommended one
 */
// it should only fire when we select/assign a image/video to a step
export function trackImageSelected(
  space: string,
  location: ImageSelectionLocation,
  isRecommended: boolean,
): void {
  // Validate inputs
  if (space === null || space === undefined || space === '') {
    space = 'unknown';
  }

  const payload: EventDataForType<GTMEventType.IMAGE_SELECTED> = {
    ImageSelected_Space: space,
    ImageSelected_Location: location,
    ImageSelected_recommendedImage: isRecommended,
  };

  sendGTMEvent(GTMEventType.IMAGE_SELECTED, payload);
}

/**
 * Tracks when a user selects an image by specifying the source
 *
 * Example usage:
 * ```
 * trackImageSelection('my-space', 'cover', 'recommended');
 * ```
 *
 * @param space - The name of the space the image was selected in
 * @param location - Where the image was selected for ('step' or 'cover')
 * @param source - The source of the selected image
 */
export function trackImageSelection(
  space: string,
  location: ImageSelectionLocation,
  source: GallerySource,
): void {
  const isRecommended: boolean = source === 'recommended';
  trackImageSelected(space, location, isRecommended);
}

/**
 * Interface for image archive tracking data
 */
type ImageArchiveData = {
  id: string;
  url: string;
  space: string;
};

/**
 * Tracks when an image is archived in the Media Gallery
 *
 * This function sends a tracking event when a user archives an image,
 * capturing the image ID, URL, and space it belonged to.
 *
 * Example usage:
 * ```
 * trackImageArchived({
 *   id: 'image-123',
 *   url: 'https://storage.example.com/images/image-123.jpg',
 *   space: 'my-space'
 * });
 * ```
 *
 * @param data - Data about the archived image
 */
export function trackImageArchived(data: ImageArchiveData): void {
  // Validate and sanitize inputs
  let sanitizedSpace = 'unknown';
  let sanitizedId = 'unknown';
  let sanitizedUrl = 'unknown';

  if (data.space !== null && data.space !== undefined && data.space !== '') {
    sanitizedSpace = data.space;
  }

  if (data.id !== null && data.id !== undefined && data.id !== '') {
    sanitizedId = data.id;
  }

  if (data.url !== null && data.url !== undefined && data.url !== '') {
    sanitizedUrl = data.url;
  }

  const payload: EventDataForType<GTMEventType.IMAGE_ARCHIVED> = {
    imageArchived_space: sanitizedSpace,
    imageArchived_id: sanitizedId,
    imageArchived_url: sanitizedUrl,
  };

  sendGTMEvent(GTMEventType.IMAGE_ARCHIVED, payload);
}

/**
 * Utility function to extract media tracking info from MediaGallery service types
 * This helps bridge the gap between service layer types and tracking needs
 *
 * @param mediaItem - Media item from MediaGallery API response
 * @returns Simplified media info for tracking
 */
export function extractMediaTrackingInfo(mediaItem: MediaGalleryListItem) {
  // Determine media type based on the item structure
  const mediaType: MediaType = mediaItem.images && mediaItem.images.length > 0 ? 'image' : 'video';

  return {
    groupId: mediaItem.groupId || '',
    mediaType,
    hasImages: (mediaItem.images?.length || 0) > 0,
    imageCount: mediaItem.images?.length || 0,
    // Note: Use groupId as the main identifier since MediaGalleryListItem doesn't have direct id
    // Additional fields can be extracted from the images array if needed
  };
}
